import { useState, useRef, useCallback, useEffect } from 'react';
import { BlockStack, InlineStack, Button, Text } from '@shopify/polaris';

interface OTPInputProps {
  length?: number;
  onComplete: (otp: string) => void;
  loading?: boolean;
}

export function OTPInput({ length = 6, onComplete, loading }: OTPInputProps) {
  const [otp, setOtp] = useState<string[]>(Array(length).fill(''));
  const [resendTimer, setResendTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Resend timer
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [resendTimer]);

  const handleChange = useCallback(
    (index: number, value: string) => {
      // Only allow digits
      if (value && !/^\d$/.test(value)) return;

      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      // Move to next input
      if (value && index < length - 1) {
        inputRefs.current[index + 1]?.focus();
      }

      // Check if complete
      if (newOtp.every((digit) => digit) && newOtp.join('').length === length) {
        onComplete(newOtp.join(''));
      }
    },
    [otp, length, onComplete]
  );

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace' && !otp[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    },
    [otp]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      e.preventDefault();
      const pastedData = e.clipboardData.getData('text').slice(0, length);

      if (!/^\d+$/.test(pastedData)) return;

      const newOtp = [...otp];
      pastedData.split('').forEach((digit, index) => {
        if (index < length) {
          newOtp[index] = digit;
        }
      });
      setOtp(newOtp);

      // Focus last filled input or first empty
      const lastIndex = Math.min(pastedData.length, length) - 1;
      inputRefs.current[lastIndex]?.focus();

      if (newOtp.every((digit) => digit)) {
        onComplete(newOtp.join(''));
      }
    },
    [otp, length, onComplete]
  );

  const handleResend = useCallback(() => {
    setResendTimer(60);
    setCanResend(false);
    setOtp(Array(length).fill(''));
    inputRefs.current[0]?.focus();
    // Trigger resend OTP logic from parent
  }, [length]);

  return (
    <BlockStack gap="400" inlineAlign="center">
      <InlineStack gap="200" align="center">
        {otp.map((digit, index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            disabled={loading}
            style={{
              width: '48px',
              height: '48px',
              textAlign: 'center',
              fontSize: '24px',
              fontWeight: 'bold',
              border: '2px solid #8c9196',
              borderRadius: '8px',
              outline: 'none',
              transition: 'border-color 0.2s',
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#2c6ecb';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#8c9196';
            }}
          />
        ))}
      </InlineStack>

      <InlineStack gap="200" align="center">
        {canResend ? (
          <Button variant="plain" onClick={handleResend}>
            Resend OTP
          </Button>
        ) : (
          <Text as="p" variant="bodySm" tone="subdued">
            Resend OTP in {resendTimer}s
          </Text>
        )}
      </InlineStack>
    </BlockStack>
  );
}
